import React from "react";
import {
  Tag,
  Heading,
  Lead,
  PageHero,
  Section,
  BtnPrimary,
} from "../components/UI";
import StatsBar from "../components/StatsBar";
import styles from "./About.module.css";

const TRAINING_PORTFOLIO = [
  {
    icon: "☕",
    t: "Java Full Stack",
    d: "End-to-end development covering front-end, back-end, and databases.",
  },
  {
    icon: "🔷",
    t: ".NET",
    d: "Build scalable enterprise applications with Microsoft's development framework.",
  },
  {
    icon: "🐍",
    t: "Python",
    d: "Versatile programming for web, automation, and data science.",
  },
  {
    icon: "🤖",
    t: "AI & ML",
    d: "Harness Artificial Intelligence and Machine Learning for innovation.",
  },
  {
    icon: "☁️",
    t: "Salesforce",
    d: "CRM expertise in Salesforce administration and development.",
  },
  {
    icon: "🧪",
    t: "QA Automation",
    d: "Modern automation testing tools to ensure software quality.",
  },
  {
    icon: "📊",
    t: "Power BI",
    d: "Transform data into insights with Microsoft's BI platform.",
  },
  {
    icon: "🔄",
    t: "DevOps",
    d: "Streamline development and operations with CI/CD, automation, and cloud practices.",
  },
  {
    icon: "🗄️",
    t: "Data Engineering",
    d: "Design and manage scalable data pipelines and architectures.",
  },
  {
    icon: "📈",
    t: "Business Analyst",
    d: "Develop analytical and communication skills to bridge business and technology.",
  },
];

const WHY_CHOOSE = [
  {
    icon: "🎯",
    t: "Customized Learning Paths",
    d: "Tailored programs for individuals and corporate teams.",
  },
  {
    icon: "🛠️",
    t: "Hands-On Experience",
    d: "Real-world projects and case studies.",
  },
  {
    icon: "📚",
    t: "Industry-Relevant Curriculum",
    d: "Updated to match evolving market trends.",
  },
  {
    icon: "🚀",
    t: "Career Growth Support",
    d: "Certifications, job readiness, and professional advancement.",
  },
];

const TEAM_HIGHLIGHTS = [
  { num: "10+", label: "Years of Experience" },
  { num: "200+", label: "Learners Trained" },
  { num: "50+", label: "Expert Instructors" },
  { num: "98%", label: "Learner Satisfaction" },
];

export default function About() {
  return (
    <>
      <PageHero
        tag="About Us"
        title="Transforming Careers & Organizations Through Technology Training"
        subtitle="Metawave Solutions empowers learners with practical, industry-relevant skills through individual, group, and corporate training programs."
      />

      {/* ── Story ─────────────────────────────────────────────── */}
      <Section variant="paper">
        <div className={styles.storyGrid}>
          <div>
            <Tag>Our Story</Tag>
            <Heading>Who We Are</Heading>
            <p className={styles.bodyText}>
              At Metawave Solutions, we are dedicated to transforming careers
              and organizations through individual and group training programs
              as well as corporate training solutions. Our mission is to
              empower learners with practical, industry-relevant skills that
              prepare them to excel in today's dynamic technology landscape.
            </p>
            <p className={styles.bodyText}>
              We combine expert-led instruction, hands-on projects, and
              career-focused learning paths to ensure that every participant
              gains the confidence and expertise to succeed.
            </p>
            <BtnPrimary to="/contact-us" style={{ marginTop: "1.8rem" }}>
              Work With Us
            </BtnPrimary>
          </div>

          <div className={styles.highlights}>
            {TEAM_HIGHLIGHTS.map(({ num, label }, i) => (
              <div key={i} className={styles.highlightCard}>
                <div className={styles.highlightNum}>{num}</div>
                <div className={styles.highlightLabel}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <StatsBar />

      {/* ── Training Portfolio ───────────────────────────────────── */}
      <Section variant="white">
        <Tag>Our Training Portfolio</Tag>
        <Heading>What We Teach</Heading>
        <Lead>
          We specialize in a wide range of technologies and professional
          skills, including:
        </Lead>
        <div className={styles.valuesGrid}>
          {TRAINING_PORTFOLIO.map((v, i) => (
            <div key={i} className={styles.valueCard}>
              <div className={styles.valueIcon}>{v.icon}</div>
              <h3 className={styles.valueTitle}>{v.t}</h3>
              <p className={styles.valueDesc}>{v.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Why Choose Us ─────────────────────────────────────── */}
      <Section variant="paper">
        <Tag>Why Choose Metawave Solutions?</Tag>
        <Heading>Built Around Your Growth</Heading>
        <div className={styles.valuesGrid}>
          {WHY_CHOOSE.map((v, i) => (
            <div key={i} className={styles.valueCard}>
              <div className={styles.valueIcon}>{v.icon}</div>
              <h3 className={styles.valueTitle}>{v.t}</h3>
              <p className={styles.valueDesc}>{v.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Closing Statement ─────────────────────────────────── */}
      <Section variant="dark" className={styles.sectionCenter}>
        <Lead light>
          At Metawave Solutions, we don't just deliver training — we create
          opportunities for <strong>growth, innovation, and success</strong>.
        </Lead>
      </Section>

      {/* ── Pillars ───────────────────────────────────────────── */}
      {/* <Section variant="ink">
        <Tag light>What We Do</Tag>
        <Heading light>Our Service Pillars</Heading>
        <div className={styles.pillarsGrid}>
          {[
            { t: 'HR Solutions',        d: 'Specialized HR services to clients in India across different sectors.' },
            { t: 'Staffing Services',   d: 'Highest quality support to clients and candidates — from consulting to direct placements.' },
            { t: 'BPO / Outsourcing',   d: 'Back-office services in the BPO industry serving diverse global clients.' },
            { t: 'IT Training',         d: 'State-of-the-art e-learning with certified instructors for career advancement.' },
            { t: 'Software Dev',        d: 'End-to-end custom software, web, mobile, and enterprise application development.' },
            { t: 'Direct Placement',    d: 'Permanent placement services connecting top talent with leading organizations.' },
          ].map((p, i) => (
            <div key={i} className={styles.pillar}>
              <div className={styles.pillarNum}>0{i + 1}</div>
              <h4 className={styles.pillarTitle}>{p.t}</h4>
              <p className={styles.pillarDesc}>{p.d}</p>
            </div>
          ))}
        </div>
      </Section> */}

      {/* ── CTA ───────────────────────────────────────────────── */}
      {/* <Section variant="paper" style={{ textAlign: 'center' }}>
        <Heading>Ready to Get Started?</Heading>
        <Lead style={{ margin: '0 auto 2rem' }}>
          Let's discuss how Metawave Solutions can help your organization grow.
        </Lead>
        <BtnPrimary to="/contact">Contact Us →</BtnPrimary>
      </Section> */}
    </>
  );
}
